import type {
  GridViewConfigItem,
  GridViewItem,
} from '@/services/gridView/gridView.types'
import { gridViewConfigByOrganizationScopeQuery } from '@/services/graphql/gridView'

export const GRID_VIEW_PAGE_SIZE = 100

export type GridViewPageRequest = {
  query: string
  variables: Record<string, unknown>
}

export type GridViewPageFetcher = (
  request: GridViewPageRequest,
) => Promise<{ items: GridViewConfigItem[]; nextToken: string | null }>

export const toTeamGridViewItem = (
  item: GridViewConfigItem,
): GridViewItem | null => {
  if (item.kind !== 'TEAM_VIEW') return null
  if (item.status === 'deleted') return null
  if (!item.name || !item.payload || !item.createdByUserID) return null

  return {
    id: item.id,
    organizationID: item.organizationID,
    scopeKey: item.scopeKey,
    name: item.name,
    description: item.description,
    payload: item.payload,
    payloadVersion: item.payloadVersion,
    gridSchemaVersion: item.gridSchemaVersion,
    status: item.status,
    isDefault: item.isDefault,
    createdByUserID: item.createdByUserID,
    updatedByUserID: item.updatedByUserID ?? item.createdByUserID,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    source: item.source,
  }
}

const isGridViewItem = (item: GridViewItem | null): item is GridViewItem =>
  item !== null

export const getGridViewListRequest = ({
  organizationID,
  scopeKey,
}: {
  organizationID: string
  scopeKey: string
}): GridViewPageRequest => ({
  query: gridViewConfigByOrganizationScopeQuery,
  variables: {
    organizationID,
    scopeKey,
    limit: GRID_VIEW_PAGE_SIZE,
    sortDirection: 'DESC',
    filter: {
      status: { ne: 'deleted' },
    },
  },
})

export const fetchAllGridViewPages = async ({
  request,
  fetchPage,
}: {
  request: GridViewPageRequest
  fetchPage: GridViewPageFetcher
}) => {
  let nextToken: string | null = null
  const items: GridViewItem[] = []
  const seenTokens = new Set<string>()

  do {
    if (nextToken) {
      if (seenTokens.has(nextToken)) {
        throw new Error('Detected repeated nextToken while listing grid views')
      }
      seenTokens.add(nextToken)
    }

    const page = await fetchPage({
      query: request.query,
      variables: {
        ...request.variables,
        nextToken,
      },
    })
    items.push(...page.items.map(toTeamGridViewItem).filter(isGridViewItem))
    nextToken = page.nextToken
  } while (nextToken)

  return items
}

export const listGridViewsWithFetcher = async ({
  organizationID,
  scopeKey,
  fetchPage,
}: {
  organizationID: string
  scopeKey: string
  fetchPage: GridViewPageFetcher
}) => {
  return fetchAllGridViewPages({
    request: getGridViewListRequest({ organizationID, scopeKey }),
    fetchPage,
  })
}
